/**
 * Desktop/mobile app download targets for the public /download page (#665).
 *
 * URLs are supplied at build time via REACT_APP_DOWNLOAD_* env vars and are
 * empty by default, so each platform shows "Coming soon" until a public
 * download URL exists.
 *
 * IMPORTANT: the CI release workflows publish artifacts to this repo's GitHub
 * Releases, but the repo is PRIVATE — those assets are NOT publicly downloadable
 * (they 404 without auth), and neither is the GitHub Releases API. To serve
 * genuinely public downloads, host the artifacts on a public location and point
 * these URLs at it: a public releases-only repo (e.g. infonaut/fawayo-releases),
 * a CDN / object-storage bucket, or a backend download proxy. See
 * docs/MACOS_DISTRIBUTION.md and docs/DOWNLOADS.md.
 */

export type DownloadKind = 'direct' | 'store';

export interface DownloadTarget {
  /** Platform label shown to the user. */
  platform: string;
  /** Short OS/version requirement line. */
  requirement: string;
  /** How it's obtained: a direct file download or a store listing. */
  kind: DownloadKind;
  /** Public URL (direct download or store listing). Empty ⇒ "Coming soon". */
  url?: string;
  /** Optional note (e.g. Gatekeeper first-open guidance). */
  note?: string;
}

export const DOWNLOADS: DownloadTarget[] = [
  {
    platform: 'macOS',
    requirement: 'macOS 14 (Sonoma) or later',
    kind: 'direct',
    url: process.env.REACT_APP_DOWNLOAD_MACOS,
    note: 'Notarized .dmg — open it and drag Connect Share to Applications.',
  },
  {
    platform: 'Windows',
    requirement: 'Windows 10 / 11 (64-bit)',
    kind: 'direct',
    url: process.env.REACT_APP_DOWNLOAD_WINDOWS,
    note: 'Requires WireGuard for Windows (installed separately).',
  },
  {
    platform: 'Linux',
    requirement: 'x86-64',
    kind: 'direct',
    url: process.env.REACT_APP_DOWNLOAD_LINUX,
  },
  {
    platform: 'Android',
    requirement: 'Android 8.0 or later',
    kind: 'store',
    url: process.env.REACT_APP_DOWNLOAD_ANDROID,
    note: 'Get it on Google Play.',
  },
  {
    platform: 'iOS',
    requirement: 'iOS 16 or later',
    kind: 'store',
    url: process.env.REACT_APP_DOWNLOAD_IOS,
    note: 'Download on the App Store.',
  },
];
